import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtEur } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { PiggyBank, Loader2, Search, Pencil, Check, Settings2, Download, AlertTriangle, ArrowLeft, Scale, FileText } from "lucide-react";
import { TfrSaldiTable } from "../components/tfr/TfrSaldiTable";
import { TfrQuadraturaTable } from "../components/tfr/TfrQuadraturaTable";

const selectCls = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";

const Kpi = ({ label, value, sub, tone = "slate", testid }) => (
    <div className={`rounded-lg border p-3 ${tone === "blue" ? "border-[#376DB3]/30 bg-[#376DB3]/5" : tone === "green" ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`} data-testid={testid}>
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
        <div className="font-mono-numbers text-lg font-bold text-slate-800">{value}</div>
        {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
);

function SettingsDialog({ open, onClose, data, onSaved }) {
    const [aliquota, setAliquota] = useState(23);
    const [net, setNet] = useState(true);
    useEffect(() => { if (open && data) { setAliquota(data.aliquota); setNet(data.portal_amounts_net); } }, [open, data]);
    const save = async () => {
        try {
            await api.put("/tfr/settings", { aliquota: parseFloat(aliquota), portal_amounts_net: net });
            toast.success("Parametri TFR salvati");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="tfr-settings-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">Parametri TFR</DialogTitle>
                    <DialogDescription>Usati per il netto stimato e per la quadratura col file del consulente.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs">Aliquota ritenuta (%)</Label>
                        <Input type="number" step="0.01" min="0" max="99" value={aliquota} onChange={(e) => setAliquota(e.target.value)} className="mt-1 w-32" data-testid="tfr-aliquota-input" />
                        <p className="mt-1 text-[11px] text-slate-500">Netto stimato = residuo lordo × (1 − aliquota). Nel file del consulente è il 23%.</p>
                    </div>
                    <label className="flex items-start gap-2 text-sm text-slate-700">
                        <input type="checkbox" checked={net} onChange={(e) => setNet(e.target.checked)} className="mt-1" data-testid="tfr-net-toggle" />
                        <span>Le ricevute TFR del portale sono importi <b>netti</b> erogati al dipendente (vengono lordizzati con l'aliquota prima di sottrarli dal fondo).</span>
                    </label>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={onClose}>Annulla</Button>
                        <Button onClick={save} className="bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="tfr-settings-save">Salva</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PayslipScanButton({ scan, onDone }) {
    const [status, setStatus] = useState(null);
    const running = status ? status.scan?.running : scan?.running;
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get("/tfr/payslips/status");
                setStatus(data);
                if (!data.scan?.running) { clearInterval(t); onDone(); toast.success(`Cedolini TFR letti: ${data.scan?.found ?? 0} su ${data.scan?.total ?? 0}`); }
            } catch { clearInterval(t); }
        }, 2500);
        return () => clearInterval(t);
    }, [running, onDone]);
    const start = async () => {
        try {
            await api.post("/tfr/payslips/rescan", { only_unscanned: false });
            setStatus({ scan: { running: true, done: 0, total: 0 } });
        } catch (e) { toast.error(e.response?.data?.detail || "Impossibile avviare la lettura"); }
    };
    const s = status?.scan || scan;
    return (
        <Button variant="outline" className="gap-2" onClick={start} disabled={!!running} data-testid="tfr-rescan-btn"
            title={s?.finished_at ? `Ultima lettura ${new Date(s.finished_at).toLocaleString("it-IT")}: ${s.found} cedolini TFR su ${s.total} PDF` : "Legge i cedolini TFR presenti in archivio"}>
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {running ? `Lettura cedolini ${s?.done ?? 0}/${s?.total ?? "…"}` : "Rileggi cedolini"}
        </Button>
    );
}

export default function TfrPage() {
    const [year, setYear] = useState(null);
    const [data, setData] = useState(null);
    const [recon, setRecon] = useState(null);
    const [tab, setTab] = useState("saldi");
    const [q, setQ] = useState("");
    const [onlyAdv, setOnlyAdv] = useState(false);
    const [showCessati, setShowCessati] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [extraYear, setExtraYear] = useState(null);

    const load = useCallback(async (y) => {
        try {
            const params = y ? { year: y } : {};
            const [s, r] = await Promise.all([api.get("/tfr/summary", { params }), api.get("/tfr/reconciliation", { params })]);
            setData(s.data);
            setRecon(r.data);
            if (!y) setYear(s.data.year);
        } catch { toast.error("Errore nel caricamento del fondo TFR"); }
    }, []);

    useEffect(() => { load(year); }, [year, load]);

    const years = useMemo(() => {
        const ys = new Set(data?.years || []);
        if (extraYear) ys.add(extraYear);
        return [...ys].sort((a, b) => b - a);
    }, [data, extraYear]);
    const nextYear = years.length ? Math.max(...years) + 1 : null;

    const rows = useMemo(() => {
        const s = q.trim().toLowerCase();
        return (data?.rows || []).filter((r) =>
            (showCessati || !r.cessato || r.fondo !== null || r.anticipi) &&
            (!onlyAdv || r.anticipi > 0) &&
            (!s || [r.cognome, r.nome, r.matricola].some((v) => String(v || "").toLowerCase().includes(s))));
    }, [data, q, onlyAdv, showCessati]);

    const onFondoSaved = (employeeId, fondo) => {
        setData((d) => {
            if (!d) return d;
            const k = 1 - d.aliquota / 100;
            const rowsNew = d.rows.map((r) => {
                if (r.employee_id !== employeeId) return r;
                const residuo = fondo === null ? null : Math.round((fondo - r.anticipi_lordo) * 100) / 100;
                return { ...r, fondo, fondo_source: "manual", fondo_updated_at: new Date().toISOString(), residuo, netto: residuo === null ? null : Math.round(residuo * k * 100) / 100 };
            });
            return { ...d, rows: rowsNew, loaded_count: rowsNew.filter((r) => r.fondo !== null).length, loaded: rowsNew.some((r) => r.fondo !== null) };
        });
    };

    const exportCsv = () => {
        const head = ["Matricola", "Dipendente", `Fondo al ${data.prev_year_end}`, `Anticipi ${data.year}`, "Lordo stimato", "Residuo lordo", "Netto stimato"];
        const lines = rows.map((r) => [r.matricola || "", `${r.cognome} ${r.nome}`, r.fondo ?? "", r.anticipi, r.anticipi_lordo, r.residuo ?? "", r.netto ?? ""]
            .map((v) => (typeof v === "number" ? String(v).replace(".", ",") : `"${v}"`)).join(";"));
        const blob = new Blob(["\ufeff" + [head.join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `Fondo_TFR_${data.year}.csv`;
        a.click();
    };

    if (!data) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
    const t = data.totals;
    const diffCount = recon?.employees_with_diff || 0;

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="tfr-page">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]"><PiggyBank className="h-6 w-6" /></div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Fondo TFR</h1>
                        <p className="text-sm text-slate-500">
                            Saldi al 31/12 dal consulente, anticipi TFR registrati nel portale, residuo e netto stimato. Dati riservati all'ufficio: non visibili ai dipendenti.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Link to="/acconti"><Button variant="outline" className="gap-2" data-testid="tfr-back-btn"><ArrowLeft className="h-4 w-4" /> Acconti</Button></Link>
                    <select value={year || ""} onChange={(e) => { const v = e.target.value; if (v === "__new") { setExtraYear(nextYear); setYear(nextYear); setEditMode(true); } else setYear(parseInt(v, 10)); }} className={selectCls} data-testid="tfr-year-select">
                        {years.map((y) => <option key={y} value={y}>Anno {y} (saldi al 31/12/{y - 1})</option>)}
                        {nextYear && <option value="__new">+ Nuovo anno {nextYear}</option>}
                    </select>
                    <Button variant="outline" className="gap-2" onClick={() => setShowSettings(true)} data-testid="tfr-settings-btn"><Settings2 className="h-4 w-4" /> Aliquota {data.aliquota}%</Button>
                    <PayslipScanButton scan={data.payslips?.scan} onDone={() => load(year)} />
                    <Button variant="outline" className="gap-2" onClick={exportCsv} data-testid="tfr-export-btn"><Download className="h-4 w-4" /> CSV</Button>
                    <Button onClick={() => setEditMode((v) => !v)} className={`gap-2 ${editMode ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#376DB3] hover:bg-[#2c5a96]"} text-white`} data-testid="tfr-edit-toggle">
                        {editMode ? <><Check className="h-4 w-4" /> Fine inserimento</> : <><Pencil className="h-4 w-4" /> Inserisci / aggiorna saldi</>}
                    </Button>
                </div>
            </div>

            {data.reminder?.due && (
                <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="tfr-reminder-banner">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                    <div><b>Saldi TFR {data.year} non ancora inseriti.</b> Dal 20 gennaio l'ufficio riceve un promemoria ogni 3 giorni finché i saldi al {data.prev_year_end} del consulente non vengono caricati. Usa «Inserisci / aggiorna saldi».</div>
                </div>
            )}
            {!data.reminder?.due && !data.loaded && (
                <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="tfr-empty-banner">
                    Nessun saldo inserito per il {data.year}: attiva «Inserisci / aggiorna saldi» e compila la colonna «Fondo al {data.prev_year_end}» con i dati del consulente.
                </div>
            )}
            {data.unmatched?.length > 0 && (
                <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="tfr-unmatched">
                    <b>{data.unmatched.length} righe del file non abbinate</b> a nessun dipendente: {data.unmatched.map((u) => `${u.name} (${u.matricola || "s/m"}, ${fmtEur(u.fondo)})`).join(", ")}.
                </div>
            )}

            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <Kpi label={`Fondo al ${data.prev_year_end}`} value={fmtEur(t.fondo)} sub={`${data.loaded_count} saldi su ${data.employees_count} dipendenti`} tone="blue" testid="tfr-kpi-fondo" />
                <Kpi label={`Anticipi ${data.year} (netto)`} value={fmtEur(t.anticipi)} sub={`lordo scalato ${fmtEur(t.anticipi_lordo)} · ${fmtEur(t.lordo_cedolino)} da cedolini${t.lordo_stimato ? ` · ${fmtEur(t.lordo_stimato)} stimati` : ""}`} testid="tfr-kpi-anticipi" />
                <Kpi label="Residuo lordo" value={fmtEur(t.residuo)} sub={data.payslips?.estimated_receipts ? `${data.payslips.estimated_receipts} ricevut${data.payslips.estimated_receipts === 1 ? "a" : "e"} senza cedolino (stima ${data.aliquota}%)` : `${data.payslips?.year_count || 0} cedolini TFR letti`} testid="tfr-kpi-residuo" />
                <Kpi label="Netto stimato" value={fmtEur(t.netto)} sub={`aliquota ${data.aliquota}%`} tone="green" testid="tfr-kpi-netto" />
                <Kpi label={`Quadratura ${data.year}`} value={recon ? `${diffCount} da verificare` : "—"} sub={recon ? `${recon.severity_counts.ok} ok · ${recon.severity_counts.info} solo file non aggiornato` : ""} testid="tfr-kpi-quadratura" />
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-2">
                    <button type="button" onClick={() => setTab("saldi")} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${tab === "saldi" ? "bg-[#376DB3] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="tfr-tab-saldi"><PiggyBank className="mr-1 inline h-4 w-4" /> Saldi</button>
                    <button type="button" onClick={() => setTab("quadratura")} className={`rounded-md px-3 py-1.5 text-sm font-semibold ${tab === "quadratura" ? "bg-[#376DB3] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="tfr-tab-quadratura">
                        <Scale className="mr-1 inline h-4 w-4" /> Quadratura {data.year}
                        {diffCount > 0 && <span className="ml-2 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{diffCount}</span>}
                    </button>
                    {tab === "saldi" && (
                        <div className="ml-auto flex flex-wrap items-center gap-3 text-xs text-slate-600">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente o matricola…" className="h-9 w-64 pl-8" data-testid="tfr-search" />
                            </div>
                            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={onlyAdv} onChange={(e) => setOnlyAdv(e.target.checked)} data-testid="tfr-only-adv" /> Solo con anticipi</label>
                            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={showCessati} onChange={(e) => setShowCessati(e.target.checked)} data-testid="tfr-show-cessati" /> Cessati</label>
                        </div>
                    )}
                </div>
                {tab === "saldi" ? (
                    <>
                        {editMode && <div className="border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-xs text-emerald-900" data-testid="tfr-edit-hint">Modalità inserimento: scrivi il fondo al {data.prev_year_end} e premi Invio o Tab; ogni valore viene salvato subito. Lascia vuoto per «da inserire».</div>}
                        <TfrSaldiTable rows={rows} year={data.year} prevYearEnd={data.prev_year_end} editMode={editMode} aliquota={data.aliquota} onSaved={onFondoSaved} />
                    </>
                ) : (
                    <TfrQuadraturaTable data={recon} loading={!recon} />
                )}
            </div>

            <SettingsDialog open={showSettings} onClose={() => setShowSettings(false)} data={data} onSaved={() => { setShowSettings(false); load(year); }} />
        </div>
    );
}
